import {
  BehaviorSubject,
  of,
  Subscription,
} from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ChipModule } from 'primeng/chip';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { GridCellComponent } from '../grid-cell/grid-cell.component';
import {
  TranslateModule,
} from '@ngx-translate/core';
import { EmptyStateContainerComponent } from './empty-state/empty-state-container/empty-state-container.component';
import { SkeletonModule } from 'primeng/skeleton';
import { ScrollerModule as VirtualScrollerModule } from 'primeng/scroller';
import { ProgressBarModule } from 'primeng/progressbar';
import { GridData } from '@fitness-force/enum';
import { ScrollingModule } from '@angular/cdk/scrolling';
@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectModule,
    ButtonModule,
    SelectButtonModule,
    MultiSelectModule,
    ChipModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    TableModule,
    DialogModule,
    DatePickerModule,
    RippleModule,
    StyleClassModule,
    GridCellComponent,
    SkeletonModule,
    ProgressBarModule,
    VirtualScrollerModule,
    ScrollingModule,
    EmptyStateContainerComponent,
    TranslateModule,
    AsyncPipe,
    NgClass
]
})
export class DataGridComponent implements OnInit, OnDestroy {
  changeDetectorRef = inject(ChangeDetectorRef);

  readonly columns = input(new BehaviorSubject<any[]>([]));
  readonly columnData = input(new BehaviorSubject<any[]>([]));
  readonly EmptyState = input(new BehaviorSubject<any>(null));
  readonly totalRecords = input(new BehaviorSubject({
    totalPage: 10,
    totalItems: 10,
    currentPage: 0,
}));
  readonly currentPage = input(new BehaviorSubject(null));
  readonly selectable = input(new BehaviorSubject(true));
  readonly scrollHeight = input('flex');
  readonly headerClasses = input('surface-100 font-bold border-none white-space-nowrap uppercase');
  readonly tableClasses = input('p-0 shadow-2 my-3');
  readonly groupMode = input(null);
  readonly groupRows = input(null);
  readonly paginator = input(true);
  readonly loading = input(false);
  readonly showHeader = input(true);
  readonly EmptyStateClass = input('text-center surface-0');
  readonly selectionMode = input('multiple');
  readonly rowSelectableFunction = input<((data: any) => boolean)>();
  readonly gridName = input<string>('');
  readonly resizableColumns = input<boolean>(false);
  readonly columnResizeMode = input<'fit' | 'expand'>('expand');

  
  readonly emitValue = output<{ data: any; selection: boolean; }>();
  readonly emitSelectValue = output<{ checked: boolean; }>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onAction = output<{ action: string; columnData: any; }>();
  readonly loadNextData = output<{ ps: number; pn: number; }>();
  readonly rowClickEvent = output<{ data: any; rowIndex: number; originalEvent: MouseEvent; }>();



  selectedRows = new BehaviorSubject(new Map());
  isAllSelected = new BehaviorSubject(false);

  itemsPerPage = 10;
  newPage = 0;
  pageSize: number = GridData.DefaultPageSize;
  value: boolean | undefined;

  cols: any[] | undefined;
  dataloaded: any = new BehaviorSubject([]);
  selectedValue: string | undefined;

  showCalendar: boolean = false;
  selectedTaskCategory: { name: string } | undefined;

  calendarInput: Date | undefined;

  first = 0;
  subscriptions: Subscription[] | any = [];
  isInfinityScroll = new BehaviorSubject(false);
  selectedRowData:any;

  ngOnInit(): void {
    this.subscriptions.push(this.columns());
    this.subscriptions.push(this.columnData());
    this.subscriptions.push(this.EmptyState());
    const totalRecords = this.totalRecords();
    this.subscriptions.push(totalRecords);
    totalRecords.subscribe((value: any) => {
      if (value.reset) this.first = 0;
    });
  }

  setAsObserVable(data: any) {
    return of(data);
  }

  setCheckBoxValue(i: number, d: any) {
    if (!this.isRowSelectable(d)) {
      return;
    }
    return Object.assign(d, { index: i });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackByFn(index: number, _item: any) {
    return index; // or item.id
  }

  goToNextPage(e: any) {
    const pageSizeChanged = e.rows !== this.pageSize;
    this.pageSize = e.rows;
    const pn = e.first ? e.first / this.pageSize + 1 : 1;

    if (pageSizeChanged) {
      this.clearSelection();
    }

    this.loadNextData.emit({ ps: this.pageSize, pn });
  }

  clearSelection() {
    this.isAllSelected.next(false);
    this.selectedRows.next(new Map());
    this.selectedRowData = null;
    this.emitSelectValue.emit({ checked: false });
  }

  isRowSelectable(data: any): boolean {
    const rowSelectableFunction = this.rowSelectableFunction();
    if (rowSelectableFunction) {
      return rowSelectableFunction(data);
    }
    return true; // Default to allowing selection if no function provided
  }

  selectedRow(columndata: any, selection: boolean) {
    // Check if row is selectable
    if (!this.isRowSelectable(columndata.data)) {
      return;
    }

    if (selection) {
      const tempMap = this.selectedRows.value;
      tempMap.set(columndata.data.index, columndata.data);
      this.selectedRows.next(tempMap);
      
      // Check if all selectable rows are selected
      const selectableRowCount = this.columnData().value
        .filter((row: any) => this.isRowSelectable(row)).length;
      this.isAllSelected.next(tempMap.size >= selectableRowCount);
    } else {
      const tempMap = this.selectedRows.value;
      tempMap.delete(columndata.data.index);
      this.selectedRows.next(tempMap);
      this.isAllSelected.next(false);
    }
    
    this.emitValue.emit({ data: columndata, selection: selection });
  }

  SelectAllRow(e: any) {
    if (e.checked) {
      this.isAllSelected.next(true);
      const selectableRowsMap = new Map();
      this.columnData().value.forEach((row: any, index: number) => {
        if (this.isRowSelectable(row)) {
          selectableRowsMap.set(index, { ...row, index });
        }
      });
      this.selectedRows.next(selectableRowsMap);
    } else {
      this.isAllSelected.next(false);
      this.selectedRows.next(new Map());
    }
    this.emitSelectValue.emit(e);
  }

  onActionEvent(event: any) {
    this.onAction.emit(event);
  }

  // TESTING PENDING FOR WRAPPING in single event ..
  emptyAction(event: any) {
    this.onAction.emit(event);
  }

  /**
   * Handle row click - only emit if click is not on a component cell
   */
  onRowClickHandler(event: MouseEvent, rowData: any, rowIndex: number): void {
    // Check if the click target is within a component cell
    const target = event.target as HTMLElement;
    const clickedOnComponentCell = target.closest('app-grid-cell') !== null;
    
    // Also check if click is on checkbox (selection)
    const clickedOnCheckbox = target.closest('p-tablecheckbox') !== null || 
                              target.closest('p-tableHeaderCheckbox') !== null;
    
    //Only emit row click if not clicking on component cell or checkbox
    if (!clickedOnComponentCell && !clickedOnCheckbox) {
      this.rowClickEvent.emit({ 
        data: rowData, 
        rowIndex: rowIndex,
        originalEvent: event 
      });
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: any) => {
      if (subscription instanceof Subscription) subscription.unsubscribe();
    });
  }

  isEmptyAndNull(obj: any) {
    if (!obj) return true;
    else if (Object.keys(obj).length === 0) {
      return true;
    } else return false;
  }
}
